import dayjs from 'dayjs';
import { processAuditedFinancialUnsecuredLoanData } from './helpers';
import { UW_DEBT_SCHEDULE_DATA_HEADERS } from '../editor/dealSummaryEditorConstants';

const AICA_AI_PROFILE_MAIN = 'aicaAIProfileMain';
const AICA_AI_PROFILE_MAIN_V2 = 'aicaAIProfileMainV2';
const AICA_FETCH_ORG_INFO = 'orgInfo';
const POLUS_FETCH_AUDITED_FINANCIAL_BALANCE_SHEET = 'auditedFinancialBalanceSheet';
const POLUS_FETCH_AUDITED_FINANCIAL_PROFIT_AND_LOSS = 'auditedFinancialProfitAndLoss';
const POLUS_FETCH_AUDITED_FINANCIAL_UNSECURED_LOAN = 'auditedFinancialUnsecuredLoan';
const UW_DEBT_SCHEDULE_DATA = 'debtSchedule';
const TOP_5_VENDOR_EXPENSE_LIST = 'top5VendorExpenseList';
const TOP_5_REVENUE_SOURCE_LIST = 'top5RevenueSourceList';
const DOMESTIC_REVENUE_ACROSS_STATES = 'domesticRevenueAcrossStates';
const COMPANY_METRICS_DATA = 'companyMetricsData';
const FINANCIAL_RATIO_DATA = 'financialRatioData';
const PROVISIONAL_FINANCIAL_RATIO_DATA = 'provisionalFinancialRatioData';

export interface OnePagerContext {
    uw_debt_schedule_data: any;
    ai_profile_main_data: any;
    ai_profile_main_data_v2: any;
    audited_financial_profit_and_loss: any;
    audited_financial_balance_sheet: any;
    audited_financial_unsecured_loan: any;
    orgInfo: any;
    companyMetricsData: any;
    companyMetricsLastUpdatedDate: string | null;
    topFiveVendorExpensePlots: any[];
    topFiveRevenueSourcePlots: any[];
    domesticRevenueAcrossStates: any;
    financialRatioData: any;
    provisionalFinancialRatioData: any;
    combinedDirectors: any[];
    bankingOverviewList: any[];
    encumberedCashBalance: any;
    topFiveVendorExpense: Array<{ name: string; amountDisplay: string }>;
    topFiveRevenueSource: Array<{ name: string; amountDisplay: string }>;
    uw_debt_schedule_data_headers: any[];
    formData: Record<string, any>;
}

export function buildOnePagerContext(apiData: any, formData: Record<string, any>): OnePagerContext {
    const uw_debt_schedule_data = apiData?.[UW_DEBT_SCHEDULE_DATA] ?? null;
    const ai_profile_main_data = apiData?.[AICA_AI_PROFILE_MAIN] ?? null;
    const ai_profile_main_data_v2 = apiData?.[AICA_AI_PROFILE_MAIN_V2] ?? null;
    const audited_financial_profit_and_loss = apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_PROFIT_AND_LOSS] ?? null;
    const audited_financial_balance_sheet = apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_BALANCE_SHEET] ?? null;
    const audited_financial_unsecured_loan = processAuditedFinancialUnsecuredLoanData(
        apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_UNSECURED_LOAN] ?? null,
    );
    const orgInfo = apiData?.[AICA_FETCH_ORG_INFO] ?? null;
    const companyMetricsData = apiData?.[COMPANY_METRICS_DATA]?.borrowerFinancials ?? {};
    const companyMetricsLastUpdatedDate =
        companyMetricsData?.lastUpdatedAt && dayjs(companyMetricsData?.lastUpdatedAt).isValid()
            ? dayjs(companyMetricsData?.lastUpdatedAt).format('DD MMM YYYY')
            : null;
    const topFiveVendorExpensePlots = apiData?.[TOP_5_VENDOR_EXPENSE_LIST] ?? [];
    const topFiveRevenueSourcePlots = apiData?.[TOP_5_REVENUE_SOURCE_LIST] ?? [];
    const domesticRevenueAcrossStates = apiData?.[DOMESTIC_REVENUE_ACROSS_STATES] ?? null;
    const financialRatioData = apiData?.[FINANCIAL_RATIO_DATA] ?? null;
    const provisionalFinancialRatioData = apiData?.[PROVISIONAL_FINANCIAL_RATIO_DATA] ?? {};
    const combinedDirectors = apiData?.directorBureauList ?? [];
    const bankingOverviewList = apiData?.bankingOverviewList ?? [];
    const encumberedCashBalance = companyMetricsData?.encumberedCashBalance ?? null;

    const topFiveVendorExpense: Array<{ name: string; amountDisplay: string }> = Array.isArray(topFiveVendorExpensePlots)
        ? topFiveVendorExpensePlots
        : [];
    const topFiveRevenueSource: Array<{ name: string; amountDisplay: string }> = Array.isArray(topFiveRevenueSourcePlots)
        ? topFiveRevenueSourcePlots
        : [];

    const uw_debt_schedule_data_headers =
        uw_debt_schedule_data?.headers?.filter((header: any) => UW_DEBT_SCHEDULE_DATA_HEADERS?.includes(header?.key)) ?? [];

    return {
        uw_debt_schedule_data,
        ai_profile_main_data,
        ai_profile_main_data_v2,
        audited_financial_profit_and_loss,
        audited_financial_balance_sheet,
        audited_financial_unsecured_loan,
        orgInfo,
        companyMetricsData,
        companyMetricsLastUpdatedDate,
        topFiveVendorExpensePlots,
        topFiveRevenueSourcePlots,
        domesticRevenueAcrossStates,
        financialRatioData,
        provisionalFinancialRatioData,
        combinedDirectors,
        bankingOverviewList,
        encumberedCashBalance,
        topFiveVendorExpense,
        topFiveRevenueSource,
        uw_debt_schedule_data_headers,
        formData,
    };
}
